import React, { useState, useEffect } from 'react';
import { 
  X, ChevronRight, ChevronLeft, FileText, Radar, Activity, 
  FlaskConical, BarChart3, Users, Anchor,
  CheckCircle2, Sparkles, Shield, Globe, BookOpen, Skull, Bell
} from 'lucide-react';

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Bienvenue sur CESM OSINT',
    description: 'Cet assistant va vous guider a travers les fonctionnalites du systeme. Decouvrez les nouveautes et apprenez a exploiter chaque module.',
    target: null,
    position: 'center',
    icon: Anchor,
    since: '1.0',
  },
  {
    id: 'theater',
    title: '1. Selection du Theatre',
    description: 'Choisissez votre theatre d\'analyse : Ormuz, Polynesie, Mer Rouge, Mer de Chine ou Mer Noire. Chaque theatre active les modules et indicateurs pertinents.',
    target: '[data-testid="theater-selector"]',
    position: 'bottom',
    icon: Globe,
    highlight: true,
    since: '1.0',
  },
  {
    id: 'brief',
    title: '2. Brief de Mission',
    description: 'Decrivez votre besoin d\'analyse. Le brief par defaut est pre-rempli selon le theatre choisi.',
    target: '[data-testid="tab-brief"]',
    position: 'bottom',
    icon: FileText,
    highlight: true,
    since: '1.0',
  },
  {
    id: 'scan',
    title: '3. Scan OSINT',
    description: 'Lancez le scan pour collecter les donnees depuis EIA (prix energie), GDELT (evenements geopolitiques) et 80+ sources RSS en parallele.',
    target: '[data-testid="tab-scan"]',
    position: 'bottom',
    icon: Radar,
    highlight: true,
    since: '1.0',
  },
  {
    id: 'adversary',
    title: '4. Veille Adversaires',
    description: 'Analysez les narratifs de propagande des sources adversaires (Russie, Chine, Iran). Score de propagande IA avec Claude Sonnet pour chaque article.',
    target: '[data-testid="tab-adversary"]',
    position: 'bottom',
    icon: Skull,
    highlight: true,
    since: '2.6',
  },
  {
    id: 'socmint',
    title: '5. SOCMINT',
    description: 'Veille sur les reseaux sociaux : Reddit en temps reel, catalogue de 100+ comptes Twitter, Telegram, VK. Dashboard visuel avec statistiques.',
    target: '[data-testid="tab-socmint"]',
    position: 'bottom',
    icon: Globe,
    highlight: true,
    since: '2.6',
  },
  {
    id: 'carte',
    title: '6. Carte Interactive',
    description: 'Carte Leaflet du theatre avec 3 couches : avions militaires ADS-B en temps reel, detections thermiques satellite NASA FIRMS, et flux Telegram live traduit automatiquement.',
    target: '[data-testid="tab-carte"]',
    position: 'bottom',
    icon: Globe,
    highlight: true,
    since: '2.7',
  },
  {
    id: 'alerts',
    title: '7. Alertes',
    description: 'Systeme d\'alertes en temps reel avec niveaux de severite. Notifications sur les evenements critiques detectes.',
    target: '[data-testid="tab-alerts"]',
    position: 'bottom',
    icon: Bell,
    highlight: true,
    since: '2.0',
  },
  {
    id: 'dashboard',
    title: '7. Dashboard KPI',
    description: 'Visualisez les indicateurs cles : GTI (Geopolitical Tension Index), SRI (Strait Risk Index), prix Brent/TTF, reserves strategiques.',
    target: '[data-testid="tab-dashboard"]',
    position: 'bottom',
    icon: Activity,
    highlight: true,
    since: '1.0',
  },
  {
    id: 'crisis',
    title: '8. Alerte Precoce',
    description: 'Systeme d\'alerte precoce multi-zones (EWS). Surveillez simultanement 5 zones strategiques avec score de risque 0-100.',
    target: '[data-testid="tab-crisis"]',
    position: 'bottom',
    icon: Shield,
    highlight: true,
    since: '2.3',
  },
  {
    id: 'simulation',
    title: '9. Simulation Monte Carlo',
    description: 'Simulez des scenarios geopolitiques (blocus, sanctions, attaques). 9 templates predefinis avec projections P10/P50/P90.',
    target: '[data-testid="tab-simulation"]',
    position: 'bottom',
    icon: Sparkles,
    highlight: true,
    since: '2.3',
  },
  {
    id: 'scenarios',
    title: '10. Scenarios Previsionnels',
    description: 'Generez 4 scenarios (Favorable, Median, Defavorable, Catastrophe) pour differentes hypotheses d\'evolution.',
    target: '[data-testid="tab-scenarios"]',
    position: 'bottom',
    icon: FlaskConical,
    since: '1.0',
  },
  {
    id: 'report',
    title: '11. Generation de Rapports',
    description: 'Rapports PDF complets avec analyse GDELT, simulation Monte Carlo, validation de qualite des donnees et recommandations operationnelles.',
    target: '[data-testid="tab-report"]',
    position: 'bottom',
    icon: BarChart3,
    highlight: true,
    since: '1.0',
  },
  {
    id: 'admin',
    title: 'Administration',
    description: 'Gestion des utilisateurs, sessions actives et rapports generes.',
    target: '[data-testid="tab-admin"]',
    position: 'bottom',
    icon: Users,
    adminOnly: true,
    since: '1.0',
  },
  {
    id: 'complete',
    title: 'Vous etes pret !',
    description: 'CESM OSINT est a votre disposition. Vous pouvez relancer ce tutoriel a tout moment via le bouton dans le header.',
    target: null,
    position: 'center',
    icon: CheckCircle2,
    since: '1.0',
  },
];

/**
 * Compare two semver-like strings: "2.6" > "2.3" > "1.0"
 */
function isVersionNewer(stepVersion, lastVersion) {
  if (!lastVersion) return true;
  const parse = (v) => (v || '0').split('.').map(Number);
  const s = parse(stepVersion);
  const l = parse(lastVersion);
  for (let i = 0; i < Math.max(s.length, l.length); i++) {
    const sv = s[i] || 0;
    const lv = l[i] || 0;
    if (sv > lv) return true;
    if (sv < lv) return false;
  }
  return false;
}

const OnboardingTour = ({ isOpen, onClose, onComplete, isAdmin = false, currentVersion = '', lastTourVersion = '' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const steps = TOUR_STEPS.filter(step => {
    if (step.adminOnly && !isAdmin) return false;
    return true;
  });

  const step = steps[currentStep];

  // Reset step when reopening
  useEffect(() => {
    if (isOpen) setCurrentStep(0);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !step?.target) {
      setTargetRect(null);
      return;
    }

    const updateTargetPosition = () => {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        setTargetRect(null);
      }
    };

    updateTargetPosition();
    window.addEventListener('resize', updateTargetPosition);
    return () => window.removeEventListener('resize', updateTargetPosition);
  }, [currentStep, step, isOpen]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleComplete = () => {
    onComplete();
    onClose();
  };

  if (!isOpen) return null;

  const isNew = step && isVersionNewer(step.since, lastTourVersion);
  const Icon = step?.icon;

  const getTooltipPosition = () => {
    if (!targetRect || step.position === 'center') {
      return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
    const padding = 16;
    let style = { position: 'fixed' };
    switch (step.position) {
      case 'bottom':
        style.top = `${targetRect.bottom + padding}px`;
        style.left = `${targetRect.left + targetRect.width / 2}px`;
        style.transform = 'translateX(-50%)';
        break;
      case 'top':
        style.bottom = `${window.innerHeight - targetRect.top + padding}px`;
        style.left = `${targetRect.left + targetRect.width / 2}px`;
        style.transform = 'translateX(-50%)';
        break;
      default:
        break;
    }
    return style;
  };

  // Count new steps for the user
  const newStepsCount = steps.filter(s => isVersionNewer(s.since, lastTourVersion) && s.id !== 'welcome' && s.id !== 'complete').length;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/80 z-[9998]" onClick={() => onClose()} />
      
      {/* Highlight */}
      {targetRect && step.highlight && (
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
            border: isNew ? '3px solid #f59e0b' : '3px solid #0ea5e9',
            borderRadius: '8px',
            boxShadow: isNew 
              ? '0 0 0 9999px rgba(0,0,0,0.75), 0 0 20px #f59e0b'
              : '0 0 0 9999px rgba(0,0,0,0.75), 0 0 20px #0ea5e9',
            animation: 'pulse-tour 2s infinite',
          }}
        />
      )}

      {/* Arrow */}
      {targetRect && step.position !== 'center' && (
        <div
          className="fixed z-[10000]"
          style={{
            color: isNew ? '#f59e0b' : '#0ea5e9',
            top: step.position === 'bottom' ? targetRect.bottom + 2 : targetRect.top - 14,
            left: targetRect.left + targetRect.width / 2 - 12,
          }}
        >
          {step.position === 'bottom' && (
            <svg width="24" height="12" viewBox="0 0 24 12" fill="currentColor"><polygon points="12,0 24,12 0,12" /></svg>
          )}
          {step.position === 'top' && (
            <svg width="24" height="12" viewBox="0 0 24 12" fill="currentColor"><polygon points="12,12 0,0 24,0" /></svg>
          )}
        </div>
      )}

      {/* Tooltip */}
      <div
        className="fixed z-[10001] w-96 max-w-[90vw] bg-marine-void border-2 p-6 shadow-2xl"
        style={{
          ...getTooltipPosition(),
          borderColor: isNew ? '#f59e0b' : '#0ea5e9',
        }}
        data-testid="tour-tooltip"
      >
        {/* Corner accents */}
        {['top-0 left-0 border-l-2 border-t-2', 'top-0 right-0 border-r-2 border-t-2', 'bottom-0 left-0 border-l-2 border-b-2', 'bottom-0 right-0 border-r-2 border-b-2'].map((cls, i) => (
          <div key={i} className={`absolute w-4 h-4 ${cls}`} style={{ borderColor: isNew ? '#f59e0b' : '#0ea5e9' }} />
        ))}

        {/* Close */}
        <button onClick={() => onClose()} className="absolute top-2 right-2 text-white/80 hover:text-white" data-testid="tour-close-btn">
          <X className="w-5 h-5" />
        </button>

        {/* Header with NEW badge */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 border" style={{ 
            background: isNew ? 'rgba(245,158,11,0.2)' : 'rgba(14,165,233,0.2)',
            borderColor: isNew ? '#f59e0b' : '#0ea5e9' 
          }}>
            {Icon && <Icon className="w-6 h-6" style={{ color: isNew ? '#f59e0b' : '#0ea5e9' }} />}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-heading text-lg uppercase tracking-wider" style={{ color: isNew ? '#f59e0b' : '#0ea5e9' }}>
                {step.title}
              </h3>
              {isNew && step.id !== 'welcome' && step.id !== 'complete' && (
                <span className="px-2 py-0.5 bg-amber-500/30 border border-amber-500 text-amber-400 text-[10px] font-mono font-bold uppercase tracking-widest animate-pulse">
                  NOUVEAU
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Welcome: show count of new features */}
        {step.id === 'welcome' && lastTourVersion && newStepsCount > 0 && (
          <div className="mb-3 px-3 py-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 font-mono text-xs">
            {newStepsCount} nouveaute{newStepsCount > 1 ? 's' : ''} depuis la v{lastTourVersion}
          </div>
        )}

        {/* Description */}
        <p className="font-mono text-white/90 text-sm leading-relaxed mb-6">{step.description}</p>

        {/* Progress */}
        <div className="flex items-center gap-1 mb-4">
          {steps.map((s, idx) => {
            const stepIsNew = isVersionNewer(s.since, lastTourVersion) && s.id !== 'welcome' && s.id !== 'complete';
            return (
              <div
                key={idx}
                className="h-1 flex-1 transition-all"
                style={{
                  background: idx <= currentStep
                    ? (stepIsNew ? '#f59e0b' : '#0ea5e9')
                    : 'rgba(100,116,139,0.5)',
                }}
              />
            );
          })}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button onClick={() => onClose()} className="font-mono text-white/80 text-xs uppercase tracking-wider hover:text-white">
            Passer
          </button>
          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <button
                onClick={handlePrev}
                className="px-4 py-2 bg-marine-slate/30 border border-marine-slate text-white/80 font-mono text-xs uppercase tracking-wider hover:bg-marine-slate/50 flex items-center gap-1"
                data-testid="tour-prev-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Precedent
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-4 py-2 font-mono text-xs uppercase tracking-wider flex items-center gap-1"
              style={{
                background: isNew ? 'rgba(245,158,11,0.2)' : 'rgba(14,165,233,0.2)',
                borderWidth: '1px',
                borderColor: isNew ? '#f59e0b' : '#0ea5e9',
                color: isNew ? '#f59e0b' : '#0ea5e9',
              }}
              data-testid="tour-next-btn"
            >
              {currentStep === steps.length - 1 ? (
                <><Sparkles className="w-4 h-4" /> Terminer</>
              ) : (
                <>Suivant <ChevronRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
        </div>

        <p className="text-center font-mono text-white/40 text-xs mt-3">
          Etape {currentStep + 1} / {steps.length}
        </p>
      </div>

      <style>{`
        @keyframes pulse-tour {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </>
  );
};

export default OnboardingTour;
